import React from "react";
import { Button } from "@/components/ui/button";
import { CheckCircle, MapPin, Shield, Award } from "lucide-react";

const Hero = () => {
  const trustSignals = [
    { icon: Shield, text: "ICBC Claims Accepted" },
    { icon: Award, text: "Certified Specialists" },
    { icon: MapPin, text: "7885 6th St, Burnaby" },
  ];

  return (
    <section
      id="home"
      className="relative min-h-screen flex items-center gradient-hero pt-20"
      data-testid="hero-section"
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-20 left-10 w-72 h-72 bg-[#0F4C3A] rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#D1FAE5] rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Content */}
          <div className="space-y-8">
            {/* Trust Badge */}
            <div className="inline-flex items-center gap-2 bg-[#D1FAE5] text-[#064E3B] px-4 py-2 rounded-full text-sm font-medium animate-fade-up stagger-1">
              <CheckCircle className="w-4 h-4" />
              <span>Trusted by 5,000+ Patients in Burnaby</span>
            </div>

            {/* Main Heading */}
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight animate-fade-up stagger-2">
              Expert Care for{" "}
              <span className="text-[#0F4C3A]">Your Recovery</span>
            </h1>

            {/* Subheading */}
            <p className="text-lg md:text-xl text-gray-600 leading-relaxed max-w-xl animate-fade-up stagger-3">
              BC-ICE offers comprehensive physiotherapy, massage therapy, 
              kinesiology, acupuncture, and specialized TMD treatment. 
              Get back to living pain-free.
            </p>

            {/* Trust Signals */}
            <div className="flex flex-wrap gap-4 animate-fade-up stagger-4">
              {trustSignals.map((signal, index) => (
                <div
                  key={index}
                  className="flex items-center gap-2 text-gray-700"
                >
                  <signal.icon className="w-5 h-5 text-[#0F4C3A]" />
                  <span className="text-sm font-medium">{signal.text}</span>
                </div>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 animate-fade-up stagger-5">
              <a
                href="https://bc-ice.janeapp.com/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-book-btn"
              >
                <Button className="w-full sm:w-auto bg-[#0F4C3A] hover:bg-[#1A6B52] text-white rounded-full px-8 py-6 text-lg font-semibold shadow-xl hover:shadow-2xl transition-all hover:-translate-y-1">
                  Book Your Appointment
                </Button>
              </a>
              <a href="#services" data-testid="hero-services-btn">
                <Button
                  variant="outline"
                  className="w-full sm:w-auto border-2 border-[#0F4C3A] text-[#0F4C3A] hover:bg-[#0F4C3A] hover:text-white rounded-full px-8 py-6 text-lg font-semibold transition-all"
                >
                  Explore Services
                </Button>
              </a>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-3 gap-6 pt-8 border-t border-gray-200 animate-fade-up stagger-6">
              <div>
                <p className="text-3xl font-bold text-[#0F4C3A]">15+</p>
                <p className="text-sm text-gray-500">Years Experience</p>
              </div>
              <div>
                <p className="text-3xl font-bold text-[#0F4C3A]">10</p>
                <p className="text-sm text-gray-500">Expert Practitioners</p>
              </div>
              <div>
                <p className="text-3xl font-bold text-[#0F4C3A]">5</p>
                <p className="text-sm text-gray-500">Specialized Services</p>
              </div>
            </div>
          </div>

          {/* Hero Image */}
          <div className="relative animate-slide-right stagger-2">
            <div className="relative rounded-3xl overflow-hidden shadow-2xl">
              <img
                src="https://images.unsplash.com/photo-1649751361457-01d3a696c7e6?w=1200&q=85&auto=format&fit=crop"
                alt="Physiotherapist assessing a patient's knee at BC-ICE rehab clinic in Burnaby"
                className="w-full h-[500px] lg:h-[600px] object-cover"
                loading="eager"
                decoding="async"
              />
              {/* Overlay Card */}
              <div className="absolute bottom-6 left-6 right-6 bg-white/95 backdrop-blur-sm rounded-2xl p-6 shadow-lg">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-[#D1FAE5] rounded-full flex items-center justify-center">
                    <CheckCircle className="w-6 h-6 text-[#0F4C3A]" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">WorkSafe BC & ICBC</p>
                    <p className="text-sm text-gray-500">Direct billing available</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Floating Badge */}
            <div className="absolute -top-4 -right-4 bg-[#C2410C] text-white px-6 py-3 rounded-full font-semibold shadow-lg animate-bounce-subtle">
              Open Today!
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
